import type { ReactNode } from 'react';

/**
 * ReMind glyphs: hand-drawn icons in the bridge palette with an ink outline,
 * used wherever the app used stock emoji (moods, weather, rewards, games).
 */

const INK = '#2f261e';
const HONEY = '#f0a43a';
const TERRA = '#d1683f';
const TEAL = '#2e6b60';
const SKY = '#8fcbe6';
const CREAM = '#fbf6ec';
const SAGE = '#8fb98a';
const SLATE = '#7d8a99';
const STORM = '#5b6170';

const o = { stroke: INK, strokeWidth: 2, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
const line = { ...o, fill: 'none' };

const cloud = (fill: string, dy = 0) => (
  <path d={`M9 ${23 + dy} a5 5 0 0 1 1 -9.8 a7 7 0 0 1 13.4 -1.6 a5.2 5.2 0 0 1 .6 11.4 Z`} fill={fill} {...o} />
);

/** A pebble face, shared by the mood glyphs. */
const face = (fill: string, inner: ReactNode) => (
  <>
    <path d="M16 4 C25 4 29 10 29 17 C29 24 24 29 16 29 C8 29 3 24 3 17 C3 10 7 4 16 4 Z" fill={fill} {...o} />
    {inner}
  </>
);

const GLYPHS: Record<string, ReactNode> = {
  // ── inner weather ──
  sun: (
    <>
      <g {...line} stroke={HONEY} strokeWidth={2.4}>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <path key={a} d="M16 2.5 V6" transform={`rotate(${a} 16 16)`} />
        ))}
      </g>
      <circle cx="16" cy="16" r="7.5" fill={HONEY} {...o} />
      <path d="M13 17 q3 3 6 0" {...line} strokeWidth={1.6} />
    </>
  ),
  breeze: (
    <>
      <circle cx="22" cy="10" r="5" fill={HONEY} {...o} />
      <path d="M3 16 H17 a3.5 3.5 0 1 0 -3.5 -3.5" {...line} stroke={TEAL} strokeWidth={2.4} />
      <path d="M5 21 H22 a3.5 3.5 0 1 1 -3.5 3.5" {...line} stroke={TEAL} strokeWidth={2.4} />
    </>
  ),
  cloud: <>{cloud('#ffffff', 3)}</>,
  rain: (
    <>
      {cloud('#c9d3dc', -2)}
      <g {...line} stroke={TEAL} strokeWidth={2.2}>
        <path d="M10 25 l-1.5 4" /><path d="M16 25 l-1.5 4" /><path d="M22 25 l-1.5 4" />
      </g>
    </>
  ),
  storm: (
    <>
      {cloud(STORM, -2)}
      <path d="M17 19 L12 26 H16 L14 31 L21 23 H17 L19 19 Z" fill={HONEY} {...o} strokeWidth={1.6} />
    </>
  ),

  // ── rewards & app ──
  diamond: (
    <>
      <path d="M8 6 H24 L29 13 L16 28 L3 13 Z" fill={SKY} {...o} />
      <path d="M3 13 H29 M12 6 L10 13 L16 28 L22 13 L20 6" {...line} strokeWidth={1.4} />
    </>
  ),
  flame: (
    <>
      <path d="M16 3 C18 9 26 12 26 20 C26 26 21 29 16 29 C11 29 6 26 6 20 C6 14 11 13 12 8 C14 11 15 12 16 3 Z" fill={TERRA} {...o} />
      <path d="M16 15 C17 19 21 20 21 24 C21 27 19 28 16 28 C13 28 11 27 11 24 C11 21 14 20 16 15 Z" fill={HONEY} />
    </>
  ),
  spark: (
    <>
      <path d="M16 2 L19 13 L30 16 L19 19 L16 30 L13 19 L2 16 L13 13 Z" fill={HONEY} {...o} />
      <circle cx="26" cy="6" r="2" fill={TERRA} />
      <circle cx="6" cy="26" r="1.6" fill={TEAL} />
    </>
  ),
  star: <path d="M16 3 l3.8 7.8 8.6 1.2 -6.2 6 1.5 8.5 -7.7 -4 -7.7 4 1.5 -8.5 -6.2 -6 8.6 -1.2 z" fill={HONEY} {...o} />,
  heart: <path d="M16 28 C6 21 3 16 3 11 A6.5 6.5 0 0 1 16 8 A6.5 6.5 0 0 1 29 11 C29 16 26 21 16 28 Z" fill={TERRA} {...o} />,
  gift: (
    <>
      <rect x="5" y="13" width="22" height="15" rx="2" fill={TERRA} {...o} />
      <rect x="3" y="9" width="26" height="6" rx="2" fill={HONEY} {...o} />
      <path d="M16 9 V28" {...line} />
      <path d="M16 9 C12 3 7 5 9 9 M16 9 C20 3 25 5 23 9" {...line} />
    </>
  ),
  crown: (
    <>
      <path d="M4 24 L3 9 L10 15 L16 6 L22 15 L29 9 L28 24 Z" fill={HONEY} {...o} />
      <rect x="4" y="24" width="24" height="4" rx="1.5" fill={TERRA} {...o} />
    </>
  ),
  check: (
    <>
      <circle cx="16" cy="16" r="13" fill={SAGE} {...o} />
      <path d="M10 16.5 l4 4 L22.5 11" {...line} strokeWidth={2.6} />
    </>
  ),
  cross: (
    <>
      <circle cx="16" cy="16" r="13" fill="#f0b3a0" {...o} />
      <path d="M11 11 L21 21 M21 11 L11 21" {...line} strokeWidth={2.6} />
    </>
  ),
  ring: <circle cx="16" cy="16" r="10" fill="none" stroke={TERRA} strokeWidth={4.5} />,
  ex: <path d="M7 7 L25 25 M25 7 L7 25" fill="none" stroke={TEAL} strokeWidth={4.5} strokeLinecap="round" />,
  flag: (
    <>
      <path d="M7 29 V4" {...line} strokeWidth={2.4} />
      <path d="M7 5 C12 2 16 8 25 5 V16 C16 19 12 13 7 16 Z" fill={TERRA} {...o} />
    </>
  ),
  bulb: (
    <>
      <path d="M16 3 A9 9 0 0 1 21 19.5 V23 H11 V19.5 A9 9 0 0 1 16 3 Z" fill={HONEY} {...o} />
      <path d="M11 26 H21 M13 29 H19" {...line} />
      <path d="M14 15 l2 2 2 -2" {...line} strokeWidth={1.4} />
    </>
  ),
  target: (
    <>
      <circle cx="16" cy="16" r="13" fill={CREAM} {...o} />
      <circle cx="16" cy="16" r="8.5" fill={TERRA} {...o} />
      <circle cx="16" cy="16" r="4" fill={CREAM} {...o} />
      <path d="M16 16 L28 4 M24 4 h4 v4" {...line} />
    </>
  ),
  brain: (
    <>
      <path d="M16 6 C12 3 6 5 6 10 C3 11 3 16 5 18 C3 21 6 26 10 25 C11 28 15 29 16 27 C17 29 21 28 22 25 C26 26 29 21 27 18 C29 16 29 11 26 10 C26 5 20 3 16 6 Z" fill="#e7a7b4" {...o} />
      <path d="M16 6 V27 M10 12 q3 1 3 4 M22 12 q-3 1 -3 4 M9 20 q3 0 4 -2 M23 20 q-3 0 -4 -2" {...line} strokeWidth={1.4} />
    </>
  ),
  steps: (
    <>
      <path d="M8 18 C5 17 5 10 9 9 C12 9 12 16 10 18 Z" fill={TEAL} {...o} />
      <circle cx="9" cy="21.5" r="2" fill={TEAL} {...o} />
      <path d="M22 12 C19 11 19 4 23 3 C26 3 26 10 24 12 Z" fill={TEAL} {...o} />
      <circle cx="23" cy="15.5" r="2" fill={TEAL} {...o} />
      <path d="M14 28 h4" {...line} strokeDasharray="1 3" />
    </>
  ),
  lotus: (
    <>
      <path d="M16 26 C10 22 9 14 16 6 C23 14 22 22 16 26 Z" fill="#e7a7b4" {...o} />
      <path d="M16 26 C9 26 3 21 3 14 C9 14 13 18 16 26 Z" fill="#f2c7d0" {...o} />
      <path d="M16 26 C23 26 29 21 29 14 C23 14 19 18 16 26 Z" fill="#f2c7d0" {...o} />
      <path d="M5 29 H27" {...line} stroke={TEAL} />
    </>
  ),
  chat: (
    <>
      <path d="M4 7 a3 3 0 0 1 3 -3 H25 a3 3 0 0 1 3 3 V18 a3 3 0 0 1 -3 3 H13 L7 27 V21 a3 3 0 0 1 -3 -3 Z" fill={SKY} {...o} />
      <g fill={INK}><circle cx="11" cy="12.5" r="1.6" /><circle cx="16" cy="12.5" r="1.6" /><circle cx="21" cy="12.5" r="1.6" /></g>
    </>
  ),
  sprout: (
    <>
      <path d="M16 29 V14" {...line} stroke={TEAL} strokeWidth={2.4} />
      <path d="M16 17 C9 18 4 13 5 7 C11 6 16 10 16 17 Z" fill={SAGE} {...o} />
      <path d="M16 14 C17 8 22 4 28 5 C28 11 23 15 16 14 Z" fill={SAGE} {...o} />
      <path d="M8 29 H24" {...line} />
    </>
  ),
  bolt: <path d="M18 2 L6 18 H15 L13 30 L26 13 H17 Z" fill={HONEY} {...o} />,
  note: (
    <>
      <path d="M6 4 H21 L26 9 V28 H6 Z" fill={CREAM} {...o} />
      <path d="M10 13 H22 M10 18 H22 M10 23 H17" {...line} strokeWidth={1.4} />
      <path d="M21 4 V9 H26" {...line} />
    </>
  ),
  chart: (
    <>
      <rect x="4" y="17" width="6" height="11" rx="1.5" fill={SKY} {...o} />
      <rect x="13" y="10" width="6" height="18" rx="1.5" fill={HONEY} {...o} />
      <rect x="22" y="4" width="6" height="24" rx="1.5" fill={TEAL} {...o} />
    </>
  ),
  pencil: (
    <>
      <path d="M5 27 L7 20 L21 6 L26 11 L12 25 Z" fill={HONEY} {...o} />
      <path d="M5 27 L7 20 L12 25 Z" fill={CREAM} {...o} />
      <path d="M18 9 L23 14" {...line} />
    </>
  ),
  trash: (
    <>
      <path d="M7 9 H25 L23 29 H9 Z" fill={SLATE} {...o} />
      <path d="M4 9 H28 M12 9 V5 H20 V9 M13 14 V24 M19 14 V24" {...line} />
    </>
  ),
  camera: (
    <>
      <rect x="3" y="9" width="26" height="18" rx="4" fill={TEAL} {...o} />
      <path d="M11 9 L13 5 H19 L21 9" fill={TEAL} {...o} />
      <circle cx="16" cy="18" r="5.5" fill={SKY} {...o} />
      <circle cx="24.5" cy="13" r="1.4" fill={HONEY} />
    </>
  ),
  rocket: (
    <>
      <path d="M16 2 C23 7 24 16 21 24 H11 C8 16 9 7 16 2 Z" fill={CREAM} {...o} />
      <circle cx="16" cy="12" r="3" fill={SKY} {...o} />
      <path d="M11 18 L5 25 L11 24 Z M21 18 L27 25 L21 24 Z" fill={TERRA} {...o} />
      <path d="M13 24 Q16 31 19 24 Z" fill={HONEY} {...o} />
    </>
  ),
  bomb: (
    <>
      <circle cx="14" cy="19" r="10" fill="#3b3631" {...o} />
      <path d="M20 11 L23 8" {...line} strokeWidth={3} />
      <path d="M23 8 Q26 3 29 5" {...line} />
      <path d="M28 2 l1 2 2 .5 -2 1 -.5 2 -1 -2 -2 -.5 2 -1 z" fill={HONEY} {...o} strokeWidth={1} />
      <circle cx="10" cy="15" r="2" fill="#ffffff" opacity="0.5" />
    </>
  ),
  stone: (
    <>
      <path d="M5 21 C4 13 10 7 17 8 C25 9 29 15 27 22 C26 27 19 28 13 28 C8 28 5 26 5 21 Z" fill={SLATE} {...o} />
      <path d="M11 14 q3 -2 6 -1" {...line} strokeWidth={1.4} />
    </>
  ),
  paper: (
    <>
      <path d="M7 4 H21 L26 9 V28 H7 Z" fill={CREAM} {...o} transform="rotate(-6 16 16)" />
      <path d="M11 13 H21 M11 18 H21 M11 23 H18" {...line} strokeWidth={1.4} transform="rotate(-6 16 16)" />
    </>
  ),
  scissors: (
    <>
      <circle cx="8" cy="23" r="4.5" fill={TERRA} {...o} />
      <circle cx="18" cy="26" r="4.5" fill={TERRA} {...o} />
      <path d="M11 20 L27 4 M15 22 L29 10" {...line} strokeWidth={2.4} />
    </>
  ),
  handshake: (
    <>
      <path d="M2 12 L9 9 L16 14 L23 9 L30 12 V21 L23 24 L16 19 L9 24 L2 21 Z" fill={HONEY} {...o} />
      <path d="M12 17 l4 3 M15 15 l4 3" {...line} strokeWidth={1.4} />
    </>
  ),
  bag: (
    <>
      <path d="M5 10 H27 L25 29 H7 Z" fill={HONEY} {...o} />
      <path d="M11 13 V8 a5 5 0 0 1 10 0 V13" {...line} />
    </>
  ),
  backpack: (
    <>
      <rect x="6" y="8" width="20" height="21" rx="6" fill={TEAL} {...o} />
      <path d="M12 8 V5 a4 4 0 0 1 8 0 V8" {...line} />
      <rect x="10" y="17" width="12" height="7" rx="2" fill={HONEY} {...o} />
    </>
  ),
  game: (
    <>
      <path d="M9 9 H23 C28 9 30 15 30 20 C30 25 26 27 23 23 L21 21 H11 L9 23 C6 27 2 25 2 20 C2 15 4 9 9 9 Z" fill={SKY} {...o} />
      <path d="M9 13 V19 M6 16 H12" {...line} />
      <circle cx="22" cy="14" r="1.6" fill={TERRA} /><circle cx="25" cy="18" r="1.6" fill={HONEY} />
    </>
  ),
  journal: (
    <>
      <path d="M6 4 H24 a2 2 0 0 1 2 2 V28 H8 a2 2 0 0 1 -2 -2 Z" fill={TERRA} {...o} />
      <path d="M10 4 V28" {...line} />
      <rect x="13" y="9" width="9" height="5" rx="1" fill={CREAM} {...o} strokeWidth={1.4} />
    </>
  ),
  nose: (
    <>
      <path d="M4 22 C8 14 13 14 16 20 C19 14 24 14 28 22" {...line} stroke={TEAL} strokeWidth={2.6} />
      <path d="M8 12 q2 -4 4 0 M20 12 q2 -4 4 0" {...line} stroke={SKY} strokeWidth={2.2} />
    </>
  ),
  pause: (
    <>
      <circle cx="16" cy="16" r="13" fill={CREAM} {...o} />
      <rect x="11" y="10" width="3.5" height="12" rx="1.5" fill={TEAL} />
      <rect x="17.5" y="10" width="3.5" height="12" rx="1.5" fill={TEAL} />
    </>
  ),
  exhale: (
    <>
      <path d="M3 12 H18 a4 4 0 1 0 -4 -4" {...line} stroke={SKY} strokeWidth={2.4} />
      <path d="M3 18 H24 a4 4 0 1 1 -4 4" {...line} stroke={TEAL} strokeWidth={2.4} />
      <path d="M3 24 H11" {...line} stroke={SKY} strokeWidth={2.4} />
    </>
  ),
  plane: (
    <>
      <path d="M3 15 L29 4 L22 28 L15 19 Z" fill={CREAM} {...o} />
      <path d="M29 4 L15 19 L14 26 L18 22" fill={SKY} {...o} />
    </>
  ),
  boom: (
    <>
      <path d="M16 2 L19 10 L27 6 L23 14 L30 18 L22 20 L24 29 L16 23 L8 29 L10 20 L2 18 L9 14 L5 6 L13 10 Z" fill={HONEY} {...o} />
      <circle cx="16" cy="16" r="4" fill={TERRA} />
    </>
  ),

  // ── moods (pebble faces) ──
  joy: face(HONEY, <><path d="M10 14 q2.5 -3 5 0 M17 14 q2.5 -3 5 0" {...line} /><path d="M10 19 Q16 26 22 19 Z" fill={INK} {...o} /></>),
  laugh: face(HONEY, <><path d="M9 13 l4 2 -4 2 M23 13 l-4 2 4 2" {...line} /><path d="M9 19 Q16 28 23 19 Z" fill={INK} {...o} /><path d="M6 15 l-2 4 M26 15 l2 4" {...line} stroke={SKY} /></>),
  sad: face('#a9c2d6', <><circle cx="12" cy="15" r="1.8" fill={INK} /><circle cx="20" cy="15" r="1.8" fill={INK} /><path d="M11 23 q5 -4 10 0" {...line} /><path d="M10 18 q-1 3 0 4 q1 -1 0 -4" fill={SKY} stroke={TEAL} strokeWidth={1.2} /></>),
  cry: face('#a9c2d6', <><path d="M9 14 q3 2 6 0 M17 14 q3 2 6 0" {...line} /><path d="M10 24 q6 -6 12 0" {...line} /><path d="M10 16 v9 M22 16 v9" {...line} stroke={SKY} strokeWidth={2.6} /></>),
  angry: face(TERRA, <><path d="M9 11 l5 2 M23 11 l-5 2" {...line} /><circle cx="12" cy="16" r="1.8" fill={INK} /><circle cx="20" cy="16" r="1.8" fill={INK} /><path d="M11 24 q5 -4 10 0" {...line} /></>),
  scared: face('#d9d2e8', <><circle cx="12" cy="14" r="3" fill="#fff" {...o} strokeWidth={1.4} /><circle cx="20" cy="14" r="3" fill="#fff" {...o} strokeWidth={1.4} /><circle cx="12" cy="14" r="1" fill={INK} /><circle cx="20" cy="14" r="1" fill={INK} /><ellipse cx="16" cy="23" rx="3" ry="3.6" fill={INK} /></>),
  love: face('#f2c7d0', <><path d="M12 17 c-4 -3 -4 -6 -2 -7 c1 -.5 2 0 2 1 c0 -1 1 -1.5 2 -1 c2 1 2 4 -2 7 Z" fill={TERRA} {...o} strokeWidth={1.2} /><path d="M20 17 c-4 -3 -4 -6 -2 -7 c1 -.5 2 0 2 1 c0 -1 1 -1.5 2 -1 c2 1 2 4 -2 7 Z" fill={TERRA} {...o} strokeWidth={1.2} /><path d="M11 21 q5 5 10 0" {...line} /></>),
  sleepy: face('#cfd6e6', <><path d="M9 15 q3 2 6 0 M17 15 q3 2 6 0" {...line} /><ellipse cx="16" cy="22" rx="2" ry="1.4" fill={INK} /><path d="M22 4 h4 l-4 4 h4" {...line} strokeWidth={1.4} /></>),
  sick: face('#b6cf9a', <><path d="M9 14 l5 1 M23 14 l-5 1" {...line} /><path d="M10 22 q2 -2 4 0 t4 0 t4 0" {...line} /></>),
  calm: face(SAGE, <><path d="M9 15 q3 2 6 0 M17 15 q3 2 6 0" {...line} /><path d="M12 21 q4 3 8 0" {...line} /></>),
};

export type GlyphName = keyof typeof GLYPHS;

interface Props {
  name: GlyphName | string;
  size?: number;
  className?: string;
  /** Accessible label; omit for decorative use. */
  title?: string;
}

export default function Glyph({ name, size = 24, className, title }: Props) {
  const art = GLYPHS[name];
  if (!art) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      overflow="visible"
    >
      {art}
    </svg>
  );
}
